'use client'

import { Link } from '@tiptap/extension-link'
import { Placeholder } from '@tiptap/extension-placeholder'
import { type Editor, EditorContent, useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { Bold, Italic, List, ListOrdered, Strikethrough } from 'lucide-react'
import { useEffect } from 'react'

import { cn } from '@/lib/cn'

import { Separator } from '../separator'
import { Toggle } from '../toggle'

type RichTextEditorProps = {
  onChange: (value: string) => void
  value?: string
  placeholder?: string
  editable?: boolean
  isError?: boolean
  clientRender?: boolean
  isWithToolbar?: boolean
  className?: string
}

const RichTextEditor = ({
  onChange,
  value,
  placeholder,
  editable = true,
  isError = false,
  clientRender = false,
  isWithToolbar = true,
  className,
}: RichTextEditorProps) => {
  const shouldInitEditor = typeof window !== 'undefined'

  const editor = useEditor(
    shouldInitEditor
      ? {
          editorProps: {
            attributes: {
              class: cn(
                'w-full rounded-md min-h-44 max-h-[500px] resize-y overflow-auto rounded-t-none border border-system-border-primary bg-white px-3 py-2 border-t-0 text-sm ring-offset-background focus-visible:outline-none overflow-auto -mt-2',
                '[&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold',
                !editable
                  ? 'cursor-not-allowed bg-system-background-disabled'
                  : '',
                isError ? 'border-system-state-error' : '',
                className,
              ),
            },
          },
          extensions: [
            Link.configure({
              defaultProtocol: 'https',
              HTMLAttributes: {
                class:
                  'text-system-text-link underline-offset-4 hover:underline cursor-pointer',
              },
            }),
            Placeholder.configure({
              placeholder,
              emptyNodeClass:
                'first:before:text-system-text-secondary first:before:float-left first:before:content-[attr(data-placeholder)] first:before:pointer-events-none first:before:h-0',
            }),
            StarterKit.configure({
              heading: {
                levels: [1, 2],
              },
              orderedList: {
                HTMLAttributes: {
                  class: 'list-decimal pl-4',
                },
              },
              bulletList: {
                HTMLAttributes: {
                  class: 'list-disc pl-4',
                },
              },
            }),
          ],
          content: value,
          onUpdate: ({ editor }) => {
            const html = editor.getHTML()
            onChange(html)
          },
          immediatelyRender: clientRender,
          editable,
        }
      : undefined,
  )

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '')
    }
  }, [editor, value])

  return (
    <>
      {editor && isWithToolbar ? (
        <RichTextEditorToolbar
          editor={editor}
          className={cn(isError ? 'border-system-state-error' : '')}
        />
      ) : null}
      <EditorContent role="textbox" editor={editor} placeholder={placeholder} />
    </>
  )
}
RichTextEditor.displayName = 'RichTextEditor'

const RichTextEditorToolbar = ({
  editor,
  className,
}: {
  editor: Editor
  className?: string
}) => {
  return (
    <div
      className={cn(
        'flex flex-row flex-wrap items-center gap-6 gap-y-1 rounded-t-md border border-system-border-primary bg-system-background-base-contrast p-1',
        className,
      )}
    >
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 1 })}
        onPressedChange={() => {
          editor.chain().focus().toggleHeading({ level: 1 }).run()
        }}
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <span className="text-sm font-semibold text-system-text-primary">
          Título
        </span>
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive('heading', { level: 2 })}
        onPressedChange={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <span className="text-sm font-semibold text-system-text-primary">
          Subtítulo
        </span>
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive('bold')}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <Bold className="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive('italic')}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <Italic className="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive('strike')}
        onPressedChange={() => editor.chain().focus().toggleStrike().run()}
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <Strikethrough className="size-4" />
      </Toggle>
      <Separator orientation="vertical" className="h-8 w-px" />
      <Toggle
        size="sm"
        pressed={editor.isActive('bulletList')}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <List className="size-4" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={editor.isActive('orderedList')}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        tabIndex={-1}
        disabled={!editor.isEditable}
      >
        <ListOrdered className="size-4" />
      </Toggle>
    </div>
  )
}

export { RichTextEditor, type RichTextEditorProps }
